import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs/Observable";
import { ConfigurationService } from "../configuration/configuration.service";

// CO-2885 Component B (frontend) — resolver client for
// GET /api/v1/month-end-close/{tenantId}/{mecId}/snapshot-availability
//
// Contract source of truth: domains/billing/docs/co-2885-trd.md §5.1.
// The backing Lambda ships in PR #3932 (branch
// CO-2885-snapshot-availability-resolver). This client consumes the HTTP
// contract, not compiled types — the two PRs merge independently and the
// feature flag guarantees safe deploy order (TRD §10).
//
// The response shape below MUST stay in lockstep with TRD §5.1 — any
// widening on the Lambda side needs a matching interface update here.

export type MecPeriodStatus = "OPEN" | "CLOSING" | "CLOSED";
export type SheetState = "LIVE" | "SNAPSHOT" | "UNAVAILABLE";
export type SheetUnavailableReason =
  | "period-open"
  | "period-closing"
  | "snapshot-pending-ingestion"
  | "sheet-missing-from-snapshot"
  | "pre-req007-partial-snapshot";

export interface PerSheetAvailability {
  sheetCode: string;
  state: SheetState;
  // Null when state === "LIVE" or "SNAPSHOT" for a happy path. For
  // UNAVAILABLE, one of the SheetUnavailableReason literals. For LIVE /
  // SNAPSHOT the TRD's decision matrix (§5.1) still names a reason
  // (`period-open` / `period-closing`) for informational purposes, so
  // the wire type keeps the union open on either state.
  reason: SheetUnavailableReason | null;
}

export interface SnapshotAvailabilityResponse {
  mecId: string;
  tenantId: string;
  periodStatus: MecPeriodStatus;
  snapshotAvailable: boolean;
  perSheet: PerSheetAvailability[];
  // ISO-8601 UTC timestamp from the resolver — surfaced for debugging
  // + potential "as of" annotations on the placeholder card.
  resolvedAt: string;
}

@Injectable()
export class SnapshotAvailabilityService {
  private readonly billingUrl: string;

  constructor(private http: HttpClient, private config: ConfigurationService) {
    // Match BillingService's dev/local override pattern
    // (see providers/billing/billing-service.ts constructor).
    if (this.config.getConfig().isLocal) {
      this.billingUrl = this.config.getConfig().localEndpoints.billing;
    } else {
      this.billingUrl = this.config.getConfig().apiUrl;
    }
  }

  // GET /api/v1/month-end-close/{tenantId}/{mecId}/snapshot-availability
  //
  // Errors are NOT swallowed here — 5xx (or a resolver 404) surface to the
  // caller so the UI can decide. Per TRD §9.4 `@open-question:resolver-error-fallback`
  // we lean toward an error banner instead of silent fallback to LIVE, because
  // silent fallback would re-open the drift window scenario 2 explicitly
  // forbids for CLOSED periods.
  getSnapshotAvailability(
    tenantId: string,
    mecId: string
  ): Observable<SnapshotAvailabilityResponse> {
    const url = `${this.billingUrl}/month-end-close/${encodeURIComponent(
      tenantId
    )}/${encodeURIComponent(mecId)}/snapshot-availability`;
    return this.http.get<SnapshotAvailabilityResponse>(url);
  }
}



// WEBPACK FOOTER //
// ./src/providers/billing/snapshot-availability.service.ts